import { createApiUrl } from '../api'

export interface SetupIntentResponse {
  clientSecret: string
}

export interface ConfirmCardRequest {
  setupIntentId: string
}

export interface CardResponse {
  paymentMethodId: string
  brand: string
  last4: string
  expMonth: number
  expYear: number
  isDefault: boolean
}

export interface SetDefaultCardRequest {
  paymentMethodId: string
}

export class CardAPI {

  /**
   * Create a Stripe SetupIntent for adding a new card
   */
  static async createSetupIntent(accessToken: string): Promise<SetupIntentResponse> {
    const url = createApiUrl('/api/v1/payments/cards/setup-intent')

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to create setup intent: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Persist and verify a Stripe SetupIntent result for the current user
   */
  static async confirmSetupIntent(
    setupIntentId: string,
    accessToken: string
  ): Promise<void> {
    const url = createApiUrl('/api/v1/payments/cards/confirm')

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ setupIntentId }),
    })

    if (!response.ok) {
      throw new Error(`Failed to confirm saved card: ${response.statusText}`)
    }
  }

  /**
   * List all saved cards for the current user
   */
  static async listCards(accessToken: string): Promise<CardResponse[]> {
    const url = createApiUrl('/api/v1/payments/cards')

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to list cards: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Set a card as the default payment method
   */
  static async setDefaultCard(
    paymentMethodId: string,
    accessToken: string
  ): Promise<void> {
    const url = createApiUrl('/api/v1/payments/cards/default')

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ paymentMethodId }),
    })

    if (!response.ok) {
      throw new Error(`Failed to set default card: ${response.statusText}`)
    }
  }

  /**
   * Delete a saved card
   */
  static async deleteCard(
    paymentMethodId: string,
    accessToken: string
  ): Promise<void> {
    const url = createApiUrl(`/api/v1/payments/cards/${paymentMethodId}`)

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to delete card: ${response.statusText}`)
    }
  }
}
